<template>
  <svg
    role="progressbar"
    :aria-valuemin="progress?.min"
    :aria-valuemax="progress?.max"
    :aria-valuenow="progress?.value"
    :aria-label="label"
    :viewBox="viewBox"
    :class="{
      spinner: true,
      'spinner--size-xs': props.size === 'xs',
      'spinner--size-sm': props.size === 'sm',
      'spinner--size-md': props.size === 'md',
      'spinner--indeterminate': isIndeterminate,
    }"
  >
    <circle
      class="spinner__track"
      cx="0"
      cy="0"
      fill="transparent"
      :r="radius"
      :style="{ stroke: circleColor }"
      :stroke-width="strokeWidth"
    />
    <path
      :d="path"
      :stroke-width="strokeWidth"
      fill="transparent"
      stroke-linecap="round"
      :style="{ stroke: pathColor }"
    />
  </svg>
</template>

<script lang="ts">
export default {
  name: 'Spinner',
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

type COLOR = 'light' | 'dark'

const props = defineProps<{
  size: string | string | string
  /**
   * The color of the 'current progress' section of the spinner
   */
  color: COLOR | { circle: string; path: string }
  /**
   * If set, the spinner will be determinate and will show the current progress.
   * If not set, the spinner will be indeterminate and will spin forever.
   */
  progress?: {
    value: number
    min: number
    max: number
  }
  /**
   * Accessible label for what process this spinner represents
   * @example "Creating auto-track keyframes"
   */
  label: string
}>()

// Initiate the values used for the SVG coordinate system

// Work with a viewbox that is 120x120. This is because:
// - The SVG stroke widths should be 1/8, 1/10, and 1/12 of the viewbox size when
//   the spinner is XS, SM, and MD respectively
// - We want our SVG stroke widths to be whole numbers to avoid JS floating point
//   weirdness where possible
// - 120 is the lowest common multiple of 8, 10, and 12.
const VIEWBOX_SIZE = 120

// The viewbox is centered around 0,0 (again, to make things easy)
const viewBox = `-${VIEWBOX_SIZE / 2} -${VIEWBOX_SIZE / 2} ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`

const strokeWidth = computed(() => {
  if (props.size === 'xs') {
    return VIEWBOX_SIZE / 8
  }

  if (props.size === 'sm') {
    return VIEWBOX_SIZE / 10
  }

  return VIEWBOX_SIZE / 12
})

// The spinner doesn't extend to the edge of the svg viewbox - it takes up a fraction of
// the viewbox depending on its size.
const diameter = computed(() => {
  if (props.size === 'xs') {
    return VIEWBOX_SIZE * 0.75
  }

  if (props.size === 'sm') {
    return VIEWBOX_SIZE * 0.9
  }

  return VIEWBOX_SIZE * 0.95
})

// The radius needs to be slightly smaller than diameter/2, since the path
// has a nonzero width which causes the circle to actually be larger
const radius = computed(() => (diameter.value - strokeWidth.value) / 2)

/**
 * If the progress prop has not been set, then the spinner is indeterminate
 * and will spin forever.
 */
const isIndeterminate = computed<boolean>(() => !props.progress)

/**
 * The progress is normalized to a value between 0 and 1, or
 * is null if the spinner is indeterminate.
 */
const normalizedProgress = computed<null | number>(() => {
  if (!props.progress) {
    return null
  }

  const { value, min, max } = props.progress
  if (max === min) {
    throw new Error('max and min cannot be equal')
  }

  return Math.max(Math.min((value - min) / (max - min), 1), 0)
})

/**
 * Represents the x coordinate of the current progress on the
 * circle, where:
 * - 0 is in line with the center of the circle
 * - RADIUS is at the rightmost point of the circle
 * - -RADIUS is at the leftmost point of the circle
 */
const xCoordinate = computed<number | null>(() => {
  if (normalizedProgress.value === null) {
    return null
  }

  return Math.sin(normalizedProgress.value * 2 * Math.PI) * radius.value
})

/**
 * Represents the y coordinate of the current progress on the
 * circle, where:
 * - 0 is in line with the center of the circle
 * - -RADIUS is at the topmost point of the circle
 * - RADIUS is at the bottommost point of the circle
 */
const yCoordinate = computed<number | null>(() => {
  if (normalizedProgress.value === null) {
    return null
  }

  return -Math.cos(normalizedProgress.value * 2 * Math.PI) * radius.value
})

/**
 * Path used for the SVG path's `d` attribute. Will be an arc around
 * the edge of the circle, starting at the topmost point of the circle
 * and ending at the current progress point.
 */
const path = computed<string>(() => {
  if (
    normalizedProgress.value === null ||
    xCoordinate.value === null ||
    yCoordinate.value === null
  ) {
    // For indeterminate spinners, the path is a quarter of the circumference
    return `M0 ${-radius.value}A${radius.value} ${radius.value} 0 0 1 ${radius.value} 0`
  }

  // If the progress is 0, then there is no path
  if (normalizedProgress.value === 0) {
    return ''
  }

  if (normalizedProgress.value === 1) {
    // If the progress is 1, then the path is a full circle. We can't draw an
    // arc with the same start and end point, so we draw two arcs instead.

    return `M0 ${-radius.value}A${radius.value} ${radius.value} 0 0 1 ${radius.value} 0A${
      radius.value
    } ${radius.value} 0 1 1 0 ${-radius.value}`
  }

  // The large arc flag is used to determine whether the arc should
  // be drawn in the "long way" around the circle.
  const largeArcFlag = normalizedProgress.value >= 0.5 ? 1 : 0

  // Draw an arc from the top of the circle to the current progress point
  // see https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths#arcs
  return `M0 ${-radius.value}A${radius.value} ${radius.value} 0 ${largeArcFlag} 1 ${
    xCoordinate.value
  } ${yCoordinate.value}`
})

const circleColor = computed(() => {
  if (props.color === 'light' || props.color === 'dark') {
    return 'var(--color-border-default)'
  }

  return props.color.circle
})

const pathColor = computed(() => {
  if (props.color === 'light') {
    return 'var(--color-icon-inverted)'
  }

  if (props.color === 'dark') {
    return 'var(--color-icon-primary)'
  }

  return props.color.path
})
</script>

<style scoped lang="scss">
@import '@/uiKit/assets/spacings';
@import '@/uiKit/assets/heights';
.spinner {
  &--size-xs {
    height: $s-xl;
    width: $s-xl;
  }

  &--size-sm {
    height: $h-icon;
    width: $h-icon;
  }

  &--size-md {
    height: $h-md;
    width: $h-md;
  }

  &--indeterminate {
    animation: spin 1s ease-in-out infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(-45deg);
  }
  to {
    transform: rotate(315deg);
  }
}
</style>
